// ============================================================
// FILE: BrowseGardeners.js
// LOCATION: C:\gardenmarket\web\src\pages\buyer\BrowseGardeners.js
// ============================================================
// PURPOSE:
//   Lets a buyer search for gardeners by suburb and skill.
//   Suburb selection now uses SuburbSearch (type-ahead) instead
//   of a full dropdown — avoids loading 60k suburbs into the DOM.
// ============================================================

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/client';
import SuburbSearch from '../../components/SuburbSearch';

export default function BrowseGardeners() {
    const { user } = useAuth();

    const [skills,    setSkills]    = useState([]);
    const [gardeners, setGardeners] = useState([]);
    const [loading,   setLoading]   = useState(false);
    const [suburb,    setSuburb]    = useState(null);
    const [country,   setCountry]   = useState('AU');
    const [skillId,   setSkillId]   = useState('');
    const [searched,  setSearched]  = useState(false);

    useEffect(() => {
        client.get('/ref/skills').then(r => setSkills(r.data.skills));
    }, []);

    // Auto-search when suburb is selected
    useEffect(() => {
        if (!suburb) return;
        setLoading(true);
        setSearched(true);
        client.get('/gardeners/search', {
            params: { suburb_id: suburb.suburb_id, skill_id: skillId || undefined }
        })
            .then(r => setGardeners(r.data.gardeners))
            .catch(console.error)
            .finally(() => setLoading(false));
    }, [suburb, skillId]);

    return (
        <div style={styles.page}>
            <h1 style={styles.title}>Find a Gardener</h1>
            <p style={styles.sub}>Search for local gardeners by suburb</p>

            <div style={styles.filters}>
                <div style={styles.suburbField}>
                    <SuburbSearch
                        label="Suburb"
                        value={suburb}
                        onChange={setSuburb}
                        country={country}
                        onCountryChange={c => { setCountry(c); setSuburb(null); setGardeners([]); setSearched(false); }}
                        placeholder="Type suburb name..."
                        showCountry={true}
                    />
                </div>
                <div style={styles.skillField}>
                    <label style={styles.label}>Skill (optional)</label>
                    <select value={skillId} onChange={e => setSkillId(e.target.value)} style={styles.input}>
                        <option value="">All skills</option>
                        {skills.map(s => (
                            <option key={s.skill_id} value={s.skill_id}>{s.skill_name}</option>
                        ))}
                    </select>
                </div>
            </div>

            {!searched && (
                <div style={styles.empty}>
                    Search for a suburb above to find gardeners in your area.
                </div>
            )}
            {loading && <div style={styles.empty}>Searching...</div>}
            {searched && !loading && gardeners.length === 0 && (
                <div style={styles.empty}>No gardeners found in this area yet.</div>
            )}

            <div style={styles.grid}>
                {!loading && gardeners.map(g => (
                    <Link to={`/gardener-profile/${g.gardener_id}`} key={g.gardener_id} style={styles.card}>
                        <div style={styles.cardTop}>
                            <h3 style={styles.name}>{g.gardener_name}</h3>
                            {g.featured_profile && <span style={styles.featured}>✨ Featured</span>}
                        </div>
                        <div style={styles.tierRow}>
                            <span style={styles.tier}>
                                {g.tier_code === 'elite' ? '🏆 Elite' : g.tier_code === 'pro' ? '⭐ Pro' : g.tier_code === 'rising' ? '📈 Rising' : '🌱 Standard'}
                            </span>
                            {g.avg_rating && <span style={styles.rating}>⭐ {Number(g.avg_rating).toFixed(1)}</span>}
                            {g.is_verified
                                ? <span style={styles.verifiedBadge}>✓ Verified</span>
                                : <span style={styles.newBadge}>New</span>}
                        </div>
                        <p style={styles.bio}>{g.bio ? (g.bio.length > 100 ? g.bio.substring(0,100)+'...' : g.bio) : 'No bio yet.'}</p>
                        <div style={styles.meta}>
                            {g.years_experience && <span>🗓 {g.years_experience} yrs</span>}
                            {g.hourly_rate && <span>💰 ${g.hourly_rate}/hr</span>}
                            <span>✅ {g.total_jobs} jobs</span>
                        </div>
                        {g.skills && <p style={styles.skillsLine}>🛠 {g.skills}</p>}
                    </Link>
                ))}
            </div>
        </div>
    );
}

const styles = {
    page:          { maxWidth:'1000px', margin:'0 auto', padding:'32px 24px' },
    title:         { color:'#2d6a4f', marginBottom:'4px' },
    sub:           { color:'#666', marginBottom:'24px' },
    filters:       { display:'flex', gap:'16px', marginBottom:'24px', flexWrap:'wrap', alignItems:'flex-end' },
    suburbField:   { flex:2, minWidth:'280px' },
    skillField:    { flex:1, minWidth:'180px' },
    label:         { display:'block', fontWeight:'600', marginBottom:'6px', color:'#333', fontSize:'0.9rem' },
    input:         { width:'100%', padding:'10px 12px', border:'1px solid #ddd', borderRadius:'6px', fontSize:'1rem', boxSizing:'border-box' },
    empty:         { textAlign:'center', padding:'60px', color:'#888' },
    grid:          { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px,1fr))', gap:'20px' },
    card:          { display:'block', background:'white', borderRadius:'10px', padding:'20px', boxShadow:'0 2px 8px rgba(0,0,0,0.08)', border:'1px solid #eee', textDecoration:'none', color:'inherit' },
    cardTop:       { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'8px' },
    name:          { margin:0, color:'#222', fontSize:'1.05rem' },
    featured:      { fontSize:'0.75rem', backgroundColor:'#fff3cd', padding:'2px 8px', borderRadius:'10px', color:'#856404' },
    tierRow:       { display:'flex', gap:'8px', marginBottom:'10px', fontSize:'0.82rem', alignItems:'center', flexWrap:'wrap' },
    tier:          { fontWeight:'bold', color:'#2d6a4f' },
    rating:        { color:'#555' },
    verifiedBadge: { backgroundColor:'#e3f2fd', color:'#1565c0', padding:'2px 8px', borderRadius:'10px', fontSize:'0.74rem', fontWeight:600 },
    newBadge:      { backgroundColor:'#f5f5f5', color:'#999', padding:'2px 8px', borderRadius:'10px', fontSize:'0.74rem' },
    bio:           { color:'#555', fontSize:'0.88rem', lineHeight:'1.4', marginBottom:'10px' },
    meta:          { display:'flex', gap:'12px', flexWrap:'wrap', fontSize:'0.82rem', color:'#555', marginBottom:'8px' },
    skillsLine:    { fontSize:'0.8rem', color:'#666' },
};
